import { EventGS, EventGSToken } from '../core/EventsGS';
import { WrapperGS, WrapperValued } from '../core/WrapperGS';
import { toType } from '../helpers/def';

export class BoxItem extends WrapperGS implements WrapperValued {
    static TagClassName = 'gs-boxitem';

    constructor(el: HTMLElement|string) {
        super(el)
    }

    get value(): string | null { return this.attr("value"); }
    set value(value: string | null) { this.attr("value", value); }
}

export class CheckBox extends WrapperGS implements WrapperValued {
    static TagClassName = 'gs-checkbox';

    private readonly _onItemSelectedToken = new EventGSToken<BoxItem>();
    private readonly _onItemClickedToken = new EventGSToken<BoxItem>();
    public readonly onItemSelected = new EventGS<BoxItem>(this._onItemSelectedToken);
    public readonly onItemClicked = new EventGS<BoxItem>(this._onItemClickedToken);

    constructor(el: HTMLElement|string) {
        super(el);
        if (!this.hasClass(CheckBox.TagClassName)) throw new Error("Trying create CheckBox from HTMLElement without specific class in classList")
        this.onclick = (e) => this.handlerClick(e);
    }    

    inactiveAll() { this.getItems().forEach((item) => item.inactive()); }
    activeByValue(val: string, invokeEvent = true) {        
        this.getItems().filter(i => i.value === val).forEach((i) => this.activeItem(i, invokeEvent));
    }
    get value(): (string | null)[] {
        return this.getActiveItems().map((i) => i.value);
    }
    set value(value: (string | null)[]) {
        this.inactiveAll();
        this.getItems().forEach(i => {            
            if (!i.value) return;
            if (value.indexOf(i.value)<0) return;
            i.active();
        });
    }
    getAllValues() {
        const result:string[] = [];
        this.getItems().forEach((item) => {if (item.value) result.push(item.value) });
        return result;
    }
    getActiveItem(): BoxItem | undefined { return this.getActiveItems()[0]; }
    getActiveItems() { return this.getItems().filter((i) => i.isActive); }
    getItems(): BoxItem[] { return getOwnedItems(this); }
    getItemByValue(cbvalue: string): BoxItem | undefined { return this.getItems().find(i => i.value === cbvalue); }


    //*Private*//
    private handlerClick(e: MouseEvent) {
        const item = getClickedItem(this, e);
        if (!item) return;
        if (item.isActive) item.inactive(); else this.activeItem(item, true);
        this._onItemClickedToken.invoke(item);
    };

    private activeItem(item: BoxItem, invokeEvent=true) {
        if (item.isActive) return;
        item.active();
        if (invokeEvent) {
            this._onItemSelectedToken.invoke(item);
            this.el.dispatchEvent(new Event('input'));
        }
    };
}


export class BtnsBox extends WrapperGS {
    static TagClassName = 'gs-btnsbox';

    private readonly _onItemClickedToken = new EventGSToken<BoxItem>();
    public readonly onItemClicked = new EventGS<BoxItem>(this._onItemClickedToken);

    constructor(el: HTMLElement|string) {
        super(el);
        if (!this.hasClass(BtnsBox.TagClassName)) throw new Error("Trying create BtnsBox from HTMLElement without specific class in classList")
        this.onclick = (e) => this.handlerClick(e);
    }

    getAllValues() {
        const result:string[] = [];
        this.getItems().forEach((item) => { if (item.value) result.push(item.value) });
        return result;
    }    
    getItems(): BoxItem[] { return getOwnedItems(this); }
    getItemByValue(cbvalue: string): BoxItem | undefined { return this.getItems().find(i => i.value === cbvalue); }

    //*Private*//
    private handlerClick(e: MouseEvent) {
        const item = getClickedItem(this, e);
        if (item) this._onItemClickedToken.invoke(item);
    };
}


export class RadioBox extends WrapperGS implements WrapperValued {
    static TagClassName = 'gs-radiobox';       

    private readonly _onItemSelectedToken = new EventGSToken<BoxItem>();
    public readonly onItemSelected = new EventGS<BoxItem>(this._onItemSelectedToken);

    constructor(el: HTMLElement|string) {
        super(el);
        if (!this.hasClass(RadioBox.TagClassName)) throw new Error("Trying create RadioBox from HTMLElement without specific class in classList")
        this.onclick = (e) => this.handlerClick(e);
    }

    inactiveAll() { this.getItems().forEach((item) => item.inactive()); }
    activeByValue(val: string | null | undefined, invokeEvent = true) {
        this.inactiveAll();
        this.getItems().filter(i => i.value === val).forEach((i) => this.activeItem(i, invokeEvent));
    }
    get value(): string | null | undefined { return this.getActiveItem()?.value; }
    set value(value: string | null | undefined) { this.activeByValue(value); }

    getAllValues() {
        const result:string[] = [];
        this.getItems().forEach((item) => { if (item.value) result.push(item.value) });
        return result;
    }
    getActiveItem(): BoxItem | undefined { return this.getItems().find(i => i.isActive); }
    getItems(): BoxItem[] { return getOwnedItems(this); }
    getItemByValue(cbvalue: string): BoxItem | undefined { return this.getItems().find(i => i.value === cbvalue); }


    //*Private*//
    private handlerClick(e: MouseEvent) {
        const item = getClickedItem(this, e);
        if (!item) return;
        this.inactiveAll();
        this.activeItem(item, true);
    };

    private activeItem(item: BoxItem, invokeEvent = true) {
        if (item.isActive) return;
        item.active();
        if (invokeEvent) {
            this._onItemSelectedToken.invoke(item);
            this.el.dispatchEvent(new Event('input'));
        }
    };
}

function toBoxItems(wrappers: WrapperGS[]): BoxItem[] {
    const items: BoxItem[] = [];
    for (const wrapper of wrappers) {
        const item = toType(wrapper, BoxItem);
        if (item) items.push(item);
    }
    return items;
}

const boxControlSelector = [CheckBox.TagClassName, BtnsBox.TagClassName, RadioBox.TagClassName]
    .map(className => `.${className}`)
    .join(',');

function getOwnedItems(owner: WrapperGS): BoxItem[] {
    return toBoxItems(owner.q(`.${BoxItem.TagClassName}`).all)
        .filter(item => item.el.parentElement?.closest(boxControlSelector) === owner.el);
}

function getClickedItem(owner: WrapperGS, event: MouseEvent): BoxItem | undefined {
    const target = event.target as Element | null;
    if (!target?.closest || !owner.el.contains(target)) return undefined;
    if (target.closest(boxControlSelector) !== owner.el) return undefined;

    const itemElement = target.closest(`.${BoxItem.TagClassName}`);
    if (!itemElement || !owner.el.contains(itemElement)) return undefined;
    return toType(WrapperGS.Wrap(itemElement), BoxItem);
}

WrapperGS.ExtendComponentsByTagClass(BoxItem);
WrapperGS.ExtendComponentsByTagClass(CheckBox);
WrapperGS.ExtendComponentsByTagClass(BtnsBox);
WrapperGS.ExtendComponentsByTagClass(RadioBox);
